import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "@emotion/styled";
import { Button, Row, Col } from "react-bootstrap";
import swal from "sweetalert";
import Layout from "views/universal/Layout/Layout";
import { getAllUrlParams } from "utils/utils";
import { productSuggestionsOperations } from "state/ducks/store/productSuggestions";
import SuggestionCard from "./SuggestionCard";
import SuggestionModal from "./SuggestionModal";
import DuplicateModal from "./DuplicateModal";
import ProductPicker from "./ProductPicker";
import useIframeAutoHeight from "./useIframeAutoHeight";

const PRIMARY = "#429853";

const ProductSuggestions = (props) => {
  // getAllUrlParams met les noms de params en minuscule => productid
  const urlParams = getAllUrlParams(props.location.search);
  const productId = urlParams.productid;
  const history = useHistory();

  const dispatch = useDispatch();
  const establishment = useSelector((state) => state.session.establishment);
  const { items, products, ingredients, pictos, isLoading } = useSelector(
    (state) => state.store.productSuggestions
  );

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [duplicating, setDuplicating] = useState(null);

  // Affichage alphabétique : l'API renvoie les suggestions dans l'ordre du champ
  // `sort` (Product::$suggestions), hérité des créations/duplications successives
  // et donc différent d'un établissement à l'autre — illisible pour gérer les
  // rotations. Tri uniquement à l'affichage : l'ordre `sort` reste celui du front
  // client, inchangé.
  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "fr", { numeric: true, sensitivity: "base" })
      ),
    [items]
  );

  const selectedProduct = productId
    ? (products || []).find((p) => String(p.id) === String(productId))
    : null;

  useIframeAutoHeight([productId, items.length, products.length, isLoading]);

  useEffect(() => {
    if (establishment) {
      dispatch(productSuggestionsOperations.listProductsForSuggestions(establishment));
    }
  }, [dispatch, establishment]);

  useEffect(() => {
    if (productId) {
      dispatch(productSuggestionsOperations.listSuggestions(productId));
      dispatch(productSuggestionsOperations.listIngredients(productId));
      dispatch(productSuggestionsOperations.listPictos());
    }
  }, [dispatch, productId]);

  const selectProduct = (id) => {
    history.push(`/product-suggestions?productId=${id}`);
  };

  const clearProduct = () => {
    history.push("/product-suggestions");
  };

  const openCreate = () => {
    setEditing(null);
    setShowModal(true);
  };
  const openEdit = (suggestion) => {
    setEditing(suggestion);
    setShowModal(true);
  };
  const handleDelete = (suggestion) => {
    swal({
      title: "Supprimer cette suggestion ?",
      text: suggestion.name,
      icon: "warning",
      buttons: ["Annuler", "Supprimer"],
      dangerMode: true,
    }).then((confirm) => {
      if (confirm) {
        dispatch(productSuggestionsOperations.deleteSuggestion(suggestion.id));
      }
    });
  };

  if (!productId) {
    return (
      <Layout location={props.location.search}>
        <Padding>
          <ProductPicker onSelect={selectProduct} />
        </Padding>
      </Layout>
    );
  }

  return (
    <Layout location={props.location.search}>
      <Padding>
        <Header>
          <div>
            <BackBtn type="button" onClick={clearProduct}>
              ← Changer de produit
            </BackBtn>
            <Title>
              Suggestions du produit <Count>{items.length}</Count>
            </Title>
            {selectedProduct && <Sub>{selectedProduct.name}</Sub>}
            {!selectedProduct && <Sub>Produit #{productId}</Sub>}
          </div>
          <Button style={{ backgroundColor: PRIMARY, borderColor: PRIMARY }} onClick={openCreate}>
            ＋ Nouvelle suggestion
          </Button>
        </Header>

        {isLoading && items.length === 0 ? (
          <Sub>Chargement…</Sub>
        ) : items.length === 0 ? (
          <Empty>
            Aucune suggestion pour le moment. Cliquez sur « Nouvelle suggestion » pour en créer une.
          </Empty>
        ) : (
          <Row>
            {sortedItems.map((suggestion) => (
              <Col md={6} lg={4} key={suggestion.id} style={{ marginBottom: 16 }}>
                <SuggestionCard
                  suggestion={suggestion}
                  onEdit={() => openEdit(suggestion)}
                  onDelete={() => handleDelete(suggestion)}
                  onDuplicate={() => setDuplicating(suggestion)}
                />
              </Col>
            ))}
          </Row>
        )}
      </Padding>

      {showModal && (
        <SuggestionModal
          show={showModal}
          onHide={() => setShowModal(false)}
          productId={productId}
          ingredients={ingredients}
          pictos={pictos}
          suggestion={editing}
        />
      )}

      {duplicating && (
        <DuplicateModal
          show={!!duplicating}
          onHide={() => setDuplicating(null)}
          productId={productId}
          suggestion={duplicating}
        />
      )}
    </Layout>
  );
};

const Padding = styled.div`
  padding: 1.25rem;
  overflow-x: hidden;
  overflow-y: visible;
  min-height: calc(100vh - 40px);
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;
const BackBtn = styled.button`
  border: none;
  background: none;
  color: #357a43;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  margin-bottom: 6px;
  display: block;
`;
const Title = styled.h2`
  font-size: 19px;
  margin: 0;
  font-weight: 800;
  color: #1d1d1b;
`;
const Count = styled.span`
  display: inline-block;
  background: #eaf4ec;
  color: #357a43;
  font-weight: 700;
  font-size: 12px;
  border-radius: 20px;
  padding: 2px 10px;
  margin-left: 8px;
`;
const Sub = styled.p`
  margin: 2px 0 0;
  color: #6b6b68;
  font-size: 13px;
`;
const Empty = styled.div`
  padding: 28px;
  text-align: center;
  color: #6b6b68;
  background: #fff;
  border: 1px dashed #e4e4e1;
  border-radius: 10px;
`;

export default ProductSuggestions;
